import React, { useEffect, useId, useReducer, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'

import { openPortalModal } from 'src/widgets/embed/openPortalModal'
import CheckBox from './CheckBox'
import InputField from './InputField'
import { injectLocalPortalModal } from './injectLocalPortalModal'
import useValidation from './useValidation'

// Tribute is hardcoded English here — same trade-off the previous
// inline Tribute.jsx made (no v7 partner currently runs tribute in
// a non-English locale; defer i18n until one does). Consistent with
// the project_v7_polish_items entry on tribute i18n.
const TRIBUTE_TYPE_LABELS = {
  honor:  'In honor of',
  memory: 'In memory of',
}

// Empty tribute state — used both for the modal's initial form values
// when the donor hasn't added a tribute yet, and for Remove (which
// resets the committed state to this shape with enabled: false).
const emptyTributeState = tribute_types => ({
  enabled: false,
  honoree_name: '',
  tribute_type: tribute_types?.[0],
  has_recipient: false,
  given_by: '',
  recipient_name: '',
  recipient_email: '',
  recipient_message: '',
})

const reducer = (state, action) => {
  switch (action.type) {
  case 'setFieldValue':
    return { ...state, [action.field]: action.value }
  case 'reset':
    return action.value
  default:
    throw new Error('Invalid reducer action')
  }
}

// Tribute composer — pattern matches Comment.jsx: a pill + Edit/Remove
// when a tribute has been added, or an "Add a tribute" trigger when one
// hasn't. The modal portals to a host-level dialog via openPortalModal
// so it pops at host-page level in properly-embedded mode (and falls
// back to the local TW4 renderer in modes 1/3).
//
// State machine: useTribute (at App level) holds the *committed*
// tribute state; this component manages a *local* editing copy that
// initializes from the committed state on open. Save validates the
// local copy and commits via tribute.setState if valid. Cancel
// discards the local copy. Remove clears committed state (sets
// enabled: false).
const Tribute = ({
  i18n,
  tribute,
  blank_modal_src,
  widget_instance_token,
}) => {
  if (!tribute.enable_tribute) return null

  // Panel.Collection co-renders the active and transitioning panels
  // for ~330ms during step transitions, which means Tribute
  // briefly coexists on multiple steps in the React tree. useId
  // disambiguates the modal id so the openPortalModal cache doesn't
  // merge them.
  const generatedId = useId()
  const modalId = `tribute-${generatedId}:${widget_instance_token}`

  const [ portal, setPortal ] = useState(null)
  const [ localState, dispatch ] = useReducer(
    reducer, emptyTributeState(tribute.tribute_types)
  )
  // Two-click confirmation for Remove, matching Comment.jsx — native
  // confirm() can be silently blocked in non-sandboxed iframes.
  const [ confirmingRemove, setConfirmingRemove ] = useState(false)

  // Validation rules depend on which sub-fields are active. We
  // recompute the field set per render based on localState so the
  // recipient block only validates when has_recipient is checked.
  const fieldSet = { honoree_name: { required: true, maxLength: 60 } }
  if (localState.has_recipient) {
    fieldSet.given_by        = { required: true, maxLength: 60 }
    fieldSet.recipient_name  = { required: true, maxLength: 60 }
    fieldSet.recipient_email = { required: true, maxLength: 75, email: true }
    fieldSet.recipient_message = { maxLength: 2000 }
  }
  const validation = useValidation({ i18n, fieldSet })

  const inputProps = id => ({
    id: `tribute_${id}`,
    value: localState[id] ?? '',
    onChange (e) {
      dispatch({ type: 'setFieldValue', field: id, value: e.target.value })
      validation.resetErrorsFor(id)
    },
  })

  const booleanProps = id => ({
    id: `tribute_${id}`,
    checked: !!localState[id],
    onChange (e) {
      dispatch({ type: 'setFieldValue', field: id, value: e.target.checked })
    },
  })

  const honoreeError = validation.errors.honoree_name?.[0]
  const givenByError = validation.errors.given_by?.[0]
  const recipientNameError = validation.errors.recipient_name?.[0]
  const recipientEmailError = validation.errors.recipient_email?.[0]
  const recipientMessageError = validation.errors.recipient_message?.[0]

  const handleOpen = async () => {
    // Initialize local state from committed (or empty if no
    // committed tribute yet). The committed shape includes
    // enabled: true once the donor has saved at least once;
    // we don't expose enabled in the modal UI itself — the
    // presence of the modal IS the enable action.
    const initial = tribute.state.enabled
      ? tribute.state
      : emptyTributeState(tribute.tribute_types)
    dispatch({ type: 'reset', value: initial })
    validation.setErrors({})
    setConfirmingRemove(false)
    const result = await openPortalModal({
      id: modalId,
      src: blank_modal_src,
      ariaLabel: 'Add a tribute',
      maxWidth: '30rem',
      renderLocal: injectLocalPortalModal,
    })
    setPortal(result)
  }

  const handleSave = () => {
    if (!validation.validate(localState)) return
    tribute.setState({ ...localState, enabled: true })
    portal?.close()
  }

  // Auto-revert the remove-confirmation state after 3 seconds so a
  // stale "Click to confirm" doesn't sit forever waiting for a
  // second click that never comes. Effect-managed (matches Comment.jsx)
  // so the timer is cleared if the component unmounts mid-window.
  useEffect(() => {
    if (!confirmingRemove) return
    const timer = setTimeout(() => setConfirmingRemove(false), 3000)
    return () => clearTimeout(timer)
  }, [ confirmingRemove ])

  const handleRemove = () => {
    if (!confirmingRemove) {
      setConfirmingRemove(true)
      return
    }
    setConfirmingRemove(false)
    tribute.setState(emptyTributeState(tribute.tribute_types))
  }

  const summary = tribute.state.enabled && (
    `${TRIBUTE_TYPE_LABELS[tribute.state.tribute_type]} ${tribute.state.honoree_name}`
  )

  const tributeType = inputProps('tribute_type')
  const honoree = inputProps('honoree_name')

  return (
    <div>
      {tribute.state.enabled ? (
        <p className="text-success inline-flex flex-wrap items-center gap-x-2">
          <span className="inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckCircle} />
            {summary}
          </span>
          <span aria-hidden="true" className="text-gray-400">·</span>
          <button
            type="button"
            onClick={handleOpen}
            className="btn-link underline underline-offset-2 text-sm"
          >
            Edit
          </button>
          <span aria-hidden="true" className="text-gray-400">·</span>
          <button
            type="button"
            onClick={handleRemove}
            className={`btn-link underline underline-offset-2 text-sm ${
              confirmingRemove ? 'text-danger font-semibold' : 'text-gray-500'
            }`}
          >
            {confirmingRemove ? 'Click to confirm' : 'Remove'}
          </button>
        </p>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          className="btn-link underline underline-offset-2 text-start"
        >
          Dedicate this donation in honor or in memory of someone
        </button>
      )}

      {portal?.target && createPortal(
        <>
          <button
            type="button"
            className="widget-modal-close hide-in-blank"
            aria-label="Close"
            onClick={() => portal.close()}
          />
          <div className="px-3 py-4 chrome:px-4 chrome:py-5 space-y-3">
            <h2 className="text-xl font-semibold">Add a tribute</h2>

            {/* Honoree name with tribute-type prepend. The wrapping
                .input-group provides the shared border / focus ring;
                each labeled control inside lives in a .input-group-cell
                so the sr-only label and its input are adjacent in
                source order for screen readers. */}
            <div>
              <div className="input-group">
                {tribute.tribute_types?.length === 1 && (
                  <span className="mx-1 px-2 py-1 bg-gray-100 rounded text-gray-700 whitespace-nowrap">
                    {TRIBUTE_TYPE_LABELS[tribute.tribute_types[0]]}
                  </span>
                )}
                {tribute.tribute_types?.length > 1 && (
                  <div className="input-group-cell">
                    <label htmlFor={tributeType.id} className="sr-only">
                      Tribute type
                    </label>
                    <select
                      id={tributeType.id}
                      value={localState.tribute_type}
                      onChange={tributeType.onChange}
                      className="form-select input-group-cell-control w-auto bg-gray-100 field-sizing-content"
                    >
                      {tribute.tribute_types.map(type => (
                        <option key={type} value={type}>
                          {TRIBUTE_TYPE_LABELS[type]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="input-group-cell flex-1 min-w-0">
                  <label htmlFor={honoree.id} className="sr-only">
                    Honoree name
                  </label>
                  <input
                    {...honoree}
                    type="text"
                    placeholder="Honoree name"
                    className="form-control input-group-control w-full"
                    aria-invalid={honoreeError ? 'true' : undefined}
                    autoFocus
                  />
                </div>
              </div>
              {honoreeError && (
                <p className="mt-1 text-sm text-danger">{honoreeError}</p>
              )}
            </div>

            <CheckBox
              {...booleanProps('has_recipient')}
              label="Send a heartfelt message and a certificate with your donation."
              hint="A link to fill this out later will be provided in your donation receipt."
              afterLabel={tribute.modal && (
                <button
                  type="button"
                  className="btn-link underline underline-offset-2"
                  data-plg-modal-label="Tribute Examples"
                  data-plg-modal-src={tribute.modal.src}
                  data-plg-modal-id={tribute.modal.id}
                  data-plg-modal-max-width="37.5rem"
                >
                  See examples
                </button>
              )}
            />

            {localState.has_recipient && (
              <div className="space-y-3">
                <InputField
                  {...inputProps('given_by')}
                  label="Given by"
                  required
                  error={givenByError}
                />
                <div className="grid grid-cols-1 gap-x-2 gap-y-3 @roomy:grid-cols-2">
                  <InputField
                    {...inputProps('recipient_name')}
                    label="Recipient name"
                    required
                    error={recipientNameError}
                  />
                  <InputField
                    {...inputProps('recipient_email')}
                    type="email"
                    label="Recipient email"
                    required
                    error={recipientEmailError}
                  />
                </div>
                <div>
                  <label
                    htmlFor={inputProps('recipient_message').id}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Message to recipient
                  </label>
                  <textarea
                    {...inputProps('recipient_message')}
                    rows={4}
                    maxLength={2000}
                    aria-invalid={recipientMessageError ? 'true' : undefined}
                    className="form-control"
                  />
                  {recipientMessageError && (
                    <p className="mt-1 text-sm text-danger">{recipientMessageError}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => portal.close()}
                className="btn btn-outline-primary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-primary flex-1"
              >
                Save
              </button>
            </div>
          </div>
        </>,
        portal.target
      )}
    </div>
  )
}

export default Tribute
